// [posting.draft-recovery](../../../docs/product/marketplace.md#posting-draft-recovery): tab-local metadata, with Blob storage for confirmed attachments.
const PREFIX = "kontrataph:posting-draft:v1:";
const DB_NAME = "kontrataph-posting-drafts";
const STORE = "files";
const MAX_AGE = 24 * 60 * 60 * 1000;
export type DraftFiles = { images: File[]; previousImages: File[]; documents: File[]; logo: File[] };
export const emptyDraftFiles = (): DraftFiles => ({ images: [], previousImages: [], documents: [], logo: [] });
type FileDescriptor = { name: string; size: number; type: string; lastModified: number };
type StoredFile = FileDescriptor & { blob: Blob };
type FileRow = { id: string; updatedAt: number; files: Record<keyof DraftFiles, StoredFile[]> };
type Envelope = { version: number; cacheId: string; data: unknown; files: Record<keyof DraftFiles, FileDescriptor[]> };
const groups: (keyof DraftFiles)[] = ["images", "previousImages", "documents", "logo"];
const descriptor = (file: File): FileDescriptor => ({ name: file.name, size: file.size, type: file.type, lastModified: file.lastModified });
const descriptors = (files: DraftFiles) => Object.fromEntries(groups.map(group => [group, files[group].map(descriptor)]));
const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));

/** Templates declare exact object fields; empty arrays declare string arrays. */
export function matchesDraftTemplate<T>(template: T, value: unknown): value is T {
  if (Array.isArray(template)) return Array.isArray(value) && value.every(entry => matchesDraftTemplate(template.length ? template[0] : "", entry));
  if (isRecord(template)) return isRecord(value) && Object.keys(value).length === Object.keys(template).length && Object.entries(template).every(([key, sample]) => matchesDraftTemplate(sample, value[key]));
  return typeof value === typeof template && (typeof value !== "number" || Number.isFinite(value));
}

export const postingDraftKey = (actor: string, type: string, context: string | null = null) => `${PREFIX}${encodeURIComponent(actor)}:${encodeURIComponent(type)}:${encodeURIComponent(context || "personal")}`;
// File-cache handles provide uniqueness; account/context keys and server checks own access.
export function newDraftCacheId(): string {
  try {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
  } catch {
    // Keep the form usable when browser randomness is unavailable as well as storage.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

function openFiles(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "id" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Draft storage is blocked"));
  });
}

async function fileTransaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openFiles();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = action(tx.objectStore(STORE));
    tx.oncomplete = () => { db.close(); resolve(request.result); };
    tx.onabort = tx.onerror = () => { db.close(); reject(tx.error || request.error); };
  });
}

async function purgeOldFiles() {
  const active = new Set<string>();
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (!key?.startsWith(PREFIX)) continue;
    try { const value: unknown = JSON.parse(sessionStorage.getItem(key) || "null"); if (isRecord(value) && typeof value.cacheId === "string") active.add(value.cacheId); } catch { /* Malformed metadata is handled by the owning form. */ }
  }
  const rows: unknown = await fileTransaction("readonly", store => store.getAll());
  if (!Array.isArray(rows)) return;
  for (const row of rows) if (isRecord(row) && typeof row.id === "string" && typeof row.updatedAt === "number" && row.updatedAt < Date.now() - MAX_AGE && !active.has(row.id)) await deleteDraftFiles(row.id);
}

export function saveDraftMetadata(key: string, cacheId: string, data: unknown, files: DraftFiles) {
  sessionStorage.setItem(key, JSON.stringify({ version: 1, cacheId, data, files: descriptors(files) }));
}

export async function saveDraftFiles(id: string, files: DraftFiles) {
  const row: FileRow = { id, updatedAt: Date.now(), files: { images: [], previousImages: [], documents: [], logo: [] } };
  for (const group of groups) row.files[group] = files[group].map(file => ({ ...descriptor(file), blob: file }));
  await fileTransaction("readwrite", store => store.put(row));
  await purgeOldFiles();
}

export async function loadPostingDraft<T>(key: string, template: T, valid: (value: T) => boolean, upgrade?: (value: unknown) => unknown): Promise<{ data: T; files: DraftFiles; cacheId: string; missingFiles: boolean } | null> {
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  let parsed: unknown = JSON.parse(raw);
  // A form may upgrade known prior fields; strict validation still rejects corrupt drafts.
  if (isRecord(parsed) && upgrade) parsed = { ...parsed, data: upgrade(parsed.data) };
  const sample: Envelope = { version: 1, cacheId: "", data: template, files: { images: [{ name: "", size: 0, type: "", lastModified: 0 }], previousImages: [{ name: "", size: 0, type: "", lastModified: 0 }], documents: [{ name: "", size: 0, type: "", lastModified: 0 }], logo: [{ name: "", size: 0, type: "", lastModified: 0 }] } };
  if (!matchesDraftTemplate(sample, parsed) || parsed.version !== 1 || !matchesDraftTemplate(template, parsed.data) || !valid(parsed.data)) throw new Error("Invalid draft");
  const files = emptyDraftFiles();
  let missingFiles = false;
  if (groups.some(group => parsed.files[group].length)) {
    let row: unknown;
    try { row = await fileTransaction("readonly", store => store.get(parsed.cacheId)); } catch { /* Restore text even when attachments were evicted or storage is unavailable. */ }
    for (const group of groups) {
      const stored = isRecord(row) && isRecord(row.files) && Array.isArray(row.files[group]) ? row.files[group] : [];
      for (const expected of parsed.files[group]) {
        const found: unknown = stored.find((entry: unknown) => isRecord(entry) && entry.name === expected.name && entry.size === expected.size && entry.type === expected.type && entry.lastModified === expected.lastModified);
        if (isRecord(found) && found.blob instanceof Blob && found.blob.size === expected.size) files[group].push(new File([found.blob], expected.name, { type: expected.type, lastModified: expected.lastModified }));
        else missingFiles = true;
      }
    }
  }
  return { data: parsed.data, files, cacheId: parsed.cacheId, missingFiles };
}

export async function deleteDraftFiles(id: string) { await fileTransaction("readwrite", store => store.delete(id)); }
export function removeDraftMetadata(key: string) { sessionStorage.removeItem(key); }

// Notify mounted writers before deleting so a logout cannot recreate the old draft.
export function clearAccountPostingDrafts(actor: string) {
  const prefix = `${PREFIX}${encodeURIComponent(actor)}:`;
  window.dispatchEvent(new CustomEvent("posting-drafts-cleared", { detail: actor }));
  try {
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i)).filter((key): key is string => Boolean(key?.startsWith(prefix)));
    for (const key of keys) {
      try { const value: unknown = JSON.parse(sessionStorage.getItem(key) || "null"); if (isRecord(value) && typeof value.cacheId === "string") void deleteDraftFiles(value.cacheId).catch(() => {}); } catch { /* Still remove malformed metadata. */ }
      sessionStorage.removeItem(key);
    }
  } catch { /* Storage-disabled sessions have no recoverable draft. */ }
}
